import {
  PyrenzModal,
  PyrenzModalContent,
  PyrenzBlueButton,
  PyrenzMessageBox,
} from '@client/theme';
import { Box, useMediaQuery } from '@mui/material';
import { useAssistantAPI } from '@components'
import { useEffect, useRef, memo, useMemo } from 'react';
import { Virtuoso } from 'react-virtuoso';

const MemoizedMessageBox = memo(PyrenzMessageBox);

interface AssistantModalProps {
  open: boolean;
  onClose: () => void;
  onApplyCharacter?: (characterData: any) => void;
}

export function AssistantModal({
  open,
  onClose,
  onApplyCharacter,
}: AssistantModalProps) {
  const {
    messages,
    input,
    setInput,
    contextInput,
    setContextInput,
    loading,
    handleSend,
    handleClear,
    handleContextSubmit,
    handleExtract,
    lastCharacterData,
  } = useAssistantAPI();

  const virtuosoRef = useRef<any>(null);
  const isMobile = useMediaQuery('(max-width:600px)');

  const normalizedMessages = useMemo(
    () =>
      messages
        .filter((m) => m.role !== 'system')
        .map((msg, i) => {
          const role: 'user' | 'char' = msg.role === 'user' ? 'user' : 'char';
          return {
            id: typeof msg.id === 'string' ? Number(msg.id) : (msg.id ?? i),
            role,
            text: msg.text ?? msg.content ?? '',
            current: Number(msg.current) || 0,
            chunks: msg.chunks ?? [],
            isGenerate: msg.isGenerate ?? false,
            isGeneratingEmptyCharMessage:
              msg.isGeneratingEmptyCharMessage ?? false,
            name: msg.name,
          };
        }),
    [messages]
  );

  useEffect(() => {
    virtuosoRef.current?.scrollToIndex({
      index: normalizedMessages.length - 1,
      behavior: 'smooth',
    });
  }, [normalizedMessages]);

  useEffect(() => {
    if (lastCharacterData && onApplyCharacter) {
      onApplyCharacter(lastCharacterData);
    }
  }, [lastCharacterData, onApplyCharacter]);

  return (
    <PyrenzModal open={open} onClose={onClose}>
      <PyrenzModalContent
        sx={{
          width: isMobile ? '95vw' : 800,
          height: isMobile ? '90vh' : 600,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box sx={{ flex: 1, mb: 2, px: 1 }}>
          <Virtuoso
            ref={virtuosoRef}
            style={{ height: '100%', overflowX: 'hidden' }}
            totalCount={normalizedMessages.length}
            itemContent={(index) => {
              const msg = normalizedMessages[index];
              return (
                <MemoizedMessageBox
                  key={`${msg.role}-${msg.id}`}
                  role={msg.role}
                  msg={msg}
                  disableReplacement
                  isGeneratingEmptyCharMessage={
                    msg.isGeneratingEmptyCharMessage
                  }
                />
              );
            }}
            followOutput="smooth"
          />
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            gap: 1,
            mb: 2,
          }}
        >
          <textarea
            value={contextInput}
            onChange={(e) => setContextInput(e.target.value)}
            placeholder="Context via link..."
            style={{
              flex: 1,
              width: '100%',
              padding: 10,
              borderRadius: 8,
              border: '1px solid #555',
              background: '#222',
              color: '#fff',
              minHeight: 60,
              resize: 'none',
            }}
            disabled={loading}
          />
          <PyrenzBlueButton
            onClick={handleContextSubmit}
            disabled={loading}
            sx={{ flexShrink: 0 }}
          >
            Submit
          </PyrenzBlueButton>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            gap: 1,
            mt: 'auto',
          }}
        >
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Type your message..."
            style={{
              flex: 1,
              width: '100%',
              padding: 10,
              borderRadius: 8,
              border: '1px solid #555',
              background: '#222',
              color: '#fff',
              minHeight: 60,
              resize: 'none',
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSend(input);
              }
            }}
            disabled={loading}
          />
          <Box
            sx={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              gap: 1,
            }}
          >
            <PyrenzBlueButton
              onClick={() => handleSend(input)}
              disabled={loading}
            >
              {loading ? 'Sending...' : 'Send'}
            </PyrenzBlueButton>
            <PyrenzBlueButton
              onClick={handleClear}
              disabled={loading}
              color="error"
            >
              Clear
            </PyrenzBlueButton>
            <PyrenzBlueButton
              onClick={handleExtract}
              disabled={loading}
              color="secondary"
            >
              Extract
            </PyrenzBlueButton>
          </Box>
        </Box>
      </PyrenzModalContent>
    </PyrenzModal>
  );
}